import { reaction } from "mobx";
import { observer } from "mobx-react";
import { Button, Tag, Tooltip, Typography } from "antd";

import * as MSF from "../../../../../components/OFTable/filters/MultiselectFilter";
import * as NF from "../../../../../components/OFTable/filters/NumericalFilter";
import * as SF from "../../../../../components/OFTable/filters/StateFilter";
import * as DRF from "../../../../../components/OFTable/filters/DateRangeFilter";

import OFTable from "../../../../../components/OFTable/OFTable";
import { CrudTableButtons } from "../../../_common/CrudTableButtons";

import _ from "lodash";
import dayjs from "dayjs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck, faCircleXmark, faEdit, faEye, faTrash } from "@fortawesome/free-solid-svg-icons";

import { translateKey as t } from "../../../../../locales/translation";
import { CronjobController } from "../../CronjobController";
import { AppStore } from "~/stores/AppStore";

const DAY_LABELS = [
  t("lblSun", "Sun"),
  t("lblMon", "Mon"),
  t("lblTue", "Tue"),
  t("lblWed", "Wed"),
  t("lblThu", "Thu"),
  t("lblFri", "Fri"),
  t("lblSat", "Sat"),
];

const formatSchedule = (cronjob: any) => {
  const freq = cronjob?.schedule?.frequency;
  const time = cronjob?.schedule?.time || "";
  const tz = cronjob?.timezone || "UTC";
  if (freq === "daily") return `${t("lblDaily", "Daily")} ${time} ${tz}`;
  if (freq === "weekly") {
    const days = (cronjob.schedule.daysOfWeek || [])
      .slice()
      .sort((a: number, b: number) => ((a + 6) % 7) - ((b + 6) % 7))
      .map((d: number) => DAY_LABELS[d])
      .join(", ");
    return `${days || t("lblWeekly", "Weekly")} ${time} ${tz}`;
  }
  return "";
};

const CronjobTable = observer(({ store, controller }: { store: AppStore; controller: CronjobController }) => {
  const getRowIndex = (row: any) => controller.cronjobs.findIndex((c) => c._id === row._id) + 1;

  const structureWithAdditionalColumns = () => {
    const columns: Array<any> = [
      {
        Header: controller.isReadOnly ? t("lblView", "View") : t("lblEdit", "Edit"),
        id: "action",
        width: 40,
        disableSortBy: true,
        disableFilters: true,
        accessor: (row: any) => _.get(row, "_id"),
        Cell: (cronjob: any) => (
          <Button
            onClick={() => controller.openEditForm(cronjob)}
            icon={controller.isReadOnly ? <FontAwesomeIcon icon={faEye} /> : <FontAwesomeIcon icon={faEdit} />}
            size="small"
          />
        ),
        responsiveRender: "lg",
      },
      {
        Header: t("lblIndex", "Index"),
        id: "index",
        align: "right",
        width: 40,
        accessor: (row: any) => getRowIndex(row),
        Filter: NF.NumericalFilter,
        filterMethod: NF.FilteringMethod,
        Cell: (row: any) => (
          <Typography.Text disabled style={{ cursor: "pointer" }}>
            {getRowIndex(row)}
          </Typography.Text>
        ),
        responsiveRender: "lg",
      },
      {
        Header: t("lblActive", "Active"),
        width: 60,
        id: "active",
        accessor: (row: any) => _.get(row, "active", false),
        Filter: (props: any) => (
          <SF.StateFilter
            {...props}
            structure={[
              { title: t("lblActive", "Active"), value: true },
              { title: t("lblInactive", "Inactive"), value: false },
            ]}
          />
        ),
        filterMethod: (value: any, query: any) => value === query,
        Cell: (data: any, value: any) =>
          value ? (
            <FontAwesomeIcon icon={faCircleCheck} style={{ color: "#52C41A" }} />
          ) : (
            <FontAwesomeIcon icon={faCircleXmark} style={{ color: "#F5222D" }} />
          ),
        responsiveRender: "lg",
      },
      {
        Header: t("lblName", "Name"),
        id: "name",
        width: 200,
        accessor: (row: any) => _.get(row, "name", ""),
        Cell: (data: any, value: any) => value,
        enableGlobalSearch: true,
        responsiveRender: "lg",
      },
      {
        Header: t("lblType", "Type"),
        id: "type",
        width: 140,
        accessor: (row: any) => {
          const typeId = _.get(row, "type");
          const ct = controller.cronjobTypes.find((t: any) => t._id === String(typeId));
          return ct ? ct.name : "";
        },
        Filter: (props: any) => (
          <MSF.MultiselectFilter
            {...props}
            dynamicValues={() =>
              controller.cronjobTypes.map((ct: any) => ({ title: ct.name, value: ct.name }))
            }
          />
        ),
        filterMethod: MSF.FilteringMethod,
        Cell: (data: any, value: any) =>
          value ? (
            <Tag color="blue" style={{ margin: 0 }}>
              {value}
            </Tag>
          ) : null,
        responsiveRender: "lg",
      },
      {
        Header: t("lblSchedule", "Schedule"),
        id: "schedule",
        width: 240,
        accessor: (row: any) => formatSchedule(row),
        Cell: (data: any, value: any) => value,
        enableGlobalSearch: true,
        responsiveRender: "lg",
      },
      {
        Header: t("lblLastRun", "Last Run"),
        id: "lastRunAt",
        width: 200,
        accessor: (row: any) =>
          _.get(row, "lastRunAt", "") ? dayjs(_.get(row, "lastRunAt")).format("DD/MM/YYYY HH:mm") : "",
        rawValue: (row: any) => (_.get(row, "lastRunAt", 0) ? dayjs(_.get(row, "lastRunAt")).valueOf() : 0),
        excludeZero: true,
        Filter: (props: any) => <DRF.DateRangeFilter {...props} />,
        filterMethod: (value: any, query: any) => DRF.FilteringMethod(value, query, "DD/MM/YYYY HH:mm"),
        Cell: (data: any, value: any) => {
          if (!value) return <Typography.Text disabled>—</Typography.Text>;
          const status = data.lastRunStatus;
          const error = data.lastRunError;
          return (
            <span>
              <span>{value}</span>
              {status === "success" && (
                <Tag color="green" style={{ marginLeft: 6 }}>
                  {t("lblSuccess", "Success")}
                </Tag>
              )}
              {status === "failed" && (
                <Tooltip title={error || t("lblFailed", "Failed")}>
                  <Tag color="red" style={{ marginLeft: 6, cursor: "help" }}>
                    {t("lblFailed", "Failed")}
                  </Tag>
                </Tooltip>
              )}
            </span>
          );
        },
        responsiveRender: "lg",
      },
      {
        Header: (
          <Tooltip title={`${t("lblCreatedAt", "Created At")} (DD/MM/YYYY HH:mm)`}>{t("lblCreatedAt", "Created At")}</Tooltip>
        ),
        id: "createdAt",
        width: 150,
        accessor: (row: any) =>
          _.get(row, "createdAt", "") ? dayjs(_.get(row, "createdAt")).format("DD/MM/YYYY HH:mm") : "",
        rawValue: (row: any) => (_.get(row, "createdAt", 0) ? dayjs(_.get(row, "createdAt")).valueOf() : 0),
        excludeZero: true,
        Filter: (props: any) => <DRF.DateRangeFilter {...props} />,
        filterMethod: (value: any, query: any) => DRF.FilteringMethod(value, query, "DD/MM/YYYY HH:mm"),
        Cell: (data: any, value: any) => <Typography.Text style={{ cursor: "pointer" }}>{value}</Typography.Text>,
        responsiveRender: "lg",
      },
      {
        Header: (
          <Tooltip title={`${t("lblUpdatedAt", "Updated At")} (DD/MM/YYYY HH:mm)`}>{t("lblUpdatedAt", "Updated At")}</Tooltip>
        ),
        id: "updatedAt",
        width: 150,
        accessor: (row: any) =>
          _.get(row, "updatedAt", "") ? dayjs(_.get(row, "updatedAt")).format("DD/MM/YYYY HH:mm") : "",
        rawValue: (row: any) => (_.get(row, "updatedAt", 0) ? dayjs(_.get(row, "updatedAt")).valueOf() : 0),
        excludeZero: true,
        Filter: (props: any) => <DRF.DateRangeFilter {...props} />,
        filterMethod: (value: any, query: any) => DRF.FilteringMethod(value, query, "DD/MM/YYYY HH:mm"),
        Cell: (data: any, value: any) => <Typography.Text style={{ cursor: "pointer" }}>{value}</Typography.Text>,
        responsiveRender: "lg",
      },
      {
        Header: t("lblUpdatedBy", "Updated By"),
        id: "updatedBy",
        width: 150,
        accessor: (row: any) => _.get(row, "updatedBy", ""),
        Cell: (data: any, value: any) => <Typography.Text style={{ cursor: "pointer" }}>{value}</Typography.Text>,
        responsiveRender: "lg",
      },
    ];

    return columns;
  };

  const structureWithoutAdditionalColumns = () =>
    structureWithAdditionalColumns().filter(
      (item) => item.id !== "index" && item.id !== "createdAt" && item.id !== "updatedAt" && item.id !== "updatedBy"
    );

  const buildTableColumns = () => {
    reaction(
      () => controller.additionalColumnsAreVisible,
      (additionalColumnsAreVisible) => {
        if (additionalColumnsAreVisible) {
          if (controller?.tableInstance) controller.tableInstance.computeColumns(structureWithAdditionalColumns());
        } else {
          controller.tableInstance.computeColumns(structureWithoutAdditionalColumns());
        }
      }
    );
    return structureWithoutAdditionalColumns();
  };

  const getContextMenuObj = () => ({
    title: null,
    tagObj: { title: "name" },
    singleMenus: (row: any) => [
      {
        name: controller.isReadOnly ? t("lblView", "View") : t("lblEdit", "Edit"),
        type: "item",
        icon: controller.isReadOnly ? <FontAwesomeIcon icon={faEye} /> : <FontAwesomeIcon icon={faEdit} />,
        render: true,
        disabled: controller.tableInstance.multiSelectedRows.totSelected > 1,
        func: () => controller.handleContextMenuEdit(row),
      },
      {
        name: t("lblDelete", "Delete"),
        type: "item",
        icon: <FontAwesomeIcon icon={faTrash} />,
        render: true,
        disabled: controller.isReadOnly,
        func: () => controller.handleContextMenuDelete(row),
      },
    ],
  });

  const { isReadOnly, crudCommon, openEditForm } = controller;
  const tableInfo: Array<any> = crudCommon.renderTableInfo().filter((info: any) => info.key !== 1);

  return (
    <OFTable
      ndnConfig={null}
      controller={controller}
      columns={buildTableColumns()}
      dataObj="cronjobs"
      exportName={t("lblCronjobs", "Cronjobs")}
      tableInstance={(instance: any) => controller.setTableInstance(instance)}
      showCheckbox={!isReadOnly}
      editableCells={false}
      globalSearch={true}
      tableInfo={tableInfo}
      fixedColumns={4}
      tableCardHeight={85}
      onCardClick={(row: any) => openEditForm(row)}
      onRowClick={(row: any) => crudCommon.handleRowClick(row)}
      onMultiSelection={() => crudCommon.handleMultiselection()}
      multiSelectRowObj="multiSelectKeyPressed"
      shiftKeyFlag="shiftKeyPressed"
      menuButtons={[
        <CrudTableButtons
          controller={controller}
          key={"CrudTableButtons"}
          onDelete={() => controller.deleteCronjobs()}
          onCreate={() => controller.setNewCronjob()}
          createButtonTitle={t("lblCronjob", "Cronjob")}
        />,
      ]}
      contextMenuObj={getContextMenuObj()}
      contextMenuWidth={200}
    />
  );
});

export { CronjobTable };
